import { useQuery } from '@apollo/react-hooks';
import { KeycloakContext } from 'components/Secured';
import { EXCHANGE_RATES, MY_PRODUCTS_SUMMARY } from 'graphql/queries';
import { IExchangeRates } from 'models/exchange-rates';
import { FC, createContext, useContext, useMemo, useState } from 'react';

export type TUploadedProductStatus = { dataId: string; productId?: string; error?: string };

export type TAppContextValue = {
  loadingProductsSummary: boolean;
  allProductCount: number | null;
  reloadAllProductCount: () => Promise<unknown>;

  loadingExchangeRates: boolean;
  exchangeRates: IExchangeRates | null;
  setSkipExchangeRates: (bol: boolean) => void;
};

export const contextDefaultValue: TAppContextValue = {
  loadingProductsSummary: true,
  allProductCount: null,
  reloadAllProductCount: async () => {},

  loadingExchangeRates: false,
  exchangeRates: null,
  setSkipExchangeRates: () => {}
};

export const AppContext = createContext<TAppContextValue>(contextDefaultValue);

export const AppContextProvider: FC = ({ children }) => {
  const keycloakCtx = useContext(KeycloakContext);
  const isProducer = keycloakCtx.keycloak.hasResourceRole('producer');

  // Loaded only once a component that shows the Apotea margin asks for it.
  const [skipExchangeRates, setSkipExchangeRates] = useState(true);

  const {
    loading,
    data: summaryData,
    refetch: reloadSummaryData,
  } = useQuery(MY_PRODUCTS_SUMMARY, { skip: !isProducer });

  const {
    loading: loadingExchangeRates,
    data: exchangeRatesData,
  } = useQuery(EXCHANGE_RATES, { skip: skipExchangeRates });

  const exchangeRates = useMemo(() => {
    return exchangeRatesData?.exchangeRates || null;
  }, [exchangeRatesData]);

  const allProductCount = useMemo(() => {
    return (summaryData?.myProductsSummary?.totalCount ?? null) as number | null;
  }, [summaryData]);

  const value = useMemo(
    () => ({
      loadingProductsSummary: loading,
      allProductCount,
      reloadAllProductCount: reloadSummaryData,
      loadingExchangeRates,
      exchangeRates,
      setSkipExchangeRates,
    }),
    [
      loading,
      allProductCount,
      reloadSummaryData,
      loadingExchangeRates,
      exchangeRates,
      setSkipExchangeRates,
    ]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  return context;
};
